import { type ApolloClient } from "@apollo/client";
import { skipToken, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { Utils } from "@binale-tech/shared";

import { dmsDocumentAiLog } from "@domain/context/mutations/documentMutations.graphql";

const AI_PAYMENT_METHODS = ["cash", "card", "mixed", "bank_transfer", "direct_debit", "online"] as const;

export type AiPaymentMethod = (typeof AI_PAYMENT_METHODS)[number];

const AI_VENUE_TYPES = ["restaurant", "retail", "fuel_station", "other"] as const;

export type AiVenueType = (typeof AI_VENUE_TYPES)[number];

export type AiLogSource =
    | (NonNullable<Utils.BewirtungPrefill.BewirtungAiPrefillSource> & {
          payment_method: AiPaymentMethod | null;
          venue_type: AiVenueType | null;
      })
    | null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const asPaymentMethod = (value: unknown): AiPaymentMethod | null =>
    AI_PAYMENT_METHODS.find((method) => method === value) ?? null;

const asVenueType = (value: unknown): AiVenueType | null => AI_VENUE_TYPES.find((venue) => venue === value) ?? null;

// Model output is untrusted: keep only the fields that have the expected type or a known value.
export const parseAiLogSource = (raw: string | null | undefined): AiLogSource => {
    if (!raw) {
        return null;
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    const prefillSource = Utils.BewirtungPrefill.toBewirtungAiPrefillSource(parsed);
    if (!prefillSource || !isRecord(parsed)) {
        return null;
    }
    return {
        ...prefillSource,
        payment_method: asPaymentMethod(parsed.payment_method),
        venue_type: asVenueType(parsed.venue_type),
    };
};

export const useAiLogSource = (
    documentId: string | undefined,
    companyId: string | undefined,
    skip = false,
): { source: AiLogSource; isLoading: boolean } => {
    const { data, loading } = useQuery(
        dmsDocumentAiLog,
        skip || !companyId || !documentId
            ? skipToken
            : { variables: { companyId, documentId }, fetchPolicy: "no-cache" },
    );
    const raw = data?.dmsDocumentAiLog;
    const source = useMemo(() => parseAiLogSource(raw), [raw]);
    return { source, isLoading: loading };
};

// A failed request yields no source and `failed`, so the caller can open empty fields and say why.
export const fetchAiLogSource = async (
    client: ApolloClient,
    documentId: string,
    companyId: string,
): Promise<{ source: AiLogSource; failed: boolean }> => {
    const { data, error } = await client.query({
        query: dmsDocumentAiLog,
        variables: { companyId, documentId },
        fetchPolicy: "no-cache",
        errorPolicy: "all",
    });
    return { source: parseAiLogSource(data?.dmsDocumentAiLog), failed: !!error };
};
